import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import {
  supabase,
  AUTH_NEXT_STORAGE_KEY,
  OAUTH_EXCHANGE_LOCK_KEY,
  resolvePostAuthRedirectPath,
} from "../lib/supabaseClient";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { readOAuthError } from "../lib/oauthError";

function readTokensFromHash() {
  if (typeof window === "undefined" || !window.location.hash) return null;
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  if (!access_token) return null;
  return { access_token, refresh_token };
}

/**
 * Completes Google OAuth on CheepSweepz (hub hash handoff or direct PKCE callback),
 * then exchanges the Supabase access token for a Mansion API session on DHC.
 */
export default function SupabaseAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const finish = async () => {
      try {
        // A provider rejection arrives here as error params with no code, so
        // report what it said instead of a generic failure.
        const providerError =
          typeof window !== "undefined"
            ? readOAuthError(window.location.search, window.location.hash)
            : null;
        if (providerError) {
          console.error("OAuth provider rejected sign-in:", providerError);
          toast.error(providerError.message, { duration: 12000 });
          sessionStorage.removeItem(OAUTH_EXCHANGE_LOCK_KEY);
          navigate("/login", { replace: true });
          return;
        }

        if (typeof window !== "undefined") {
          const locked = sessionStorage.getItem(OAUTH_EXCHANGE_LOCK_KEY);
          if (locked === "1") return;
          sessionStorage.setItem(OAUTH_EXCHANGE_LOCK_KEY, "1");
        }

        let accessToken = readTokensFromHash()?.access_token ?? null;
        const hashTokens = readTokensFromHash();

        let sessionUser = null;

        if (hashTokens) {
          window.history.replaceState(
            null,
            "",
            `${window.location.pathname}${window.location.search}`
          );
          if (hashTokens.refresh_token && supabase) {
            const { data: setData } = await supabase.auth.setSession({
              access_token: hashTokens.access_token,
              refresh_token: hashTokens.refresh_token,
            });
            sessionUser = setData.session?.user ?? null;
          }
        } else if (supabase) {
          const code = searchParams.get("code");
          if (code) {
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
            if (exchangeError) {
              throw exchangeError;
            }
          }

          const {
            data: { session },
            error: sessionError,
          } = await supabase.auth.getSession();

          if (sessionError) {
            throw sessionError;
          }
          accessToken = session?.access_token || null;
          sessionUser = session?.user ?? null;

          if (sessionUser?.email) {
            console.log('User signed in:', sessionUser.email);
          }
        } else if (!accessToken) {
          toast.error("Google sign-in is not configured on this site.");
          navigate("/login", { replace: true });
          return;
        }

        if (!accessToken) {
          throw new Error("No Supabase session after sign-in");
        }

        const response = await axios.post(
          `${API}/auth/supabase`,
          { access_token: accessToken },
          {
            withCredentials: true,
            timeout: 20000,
            headers: {
              "X-Site-Origin": window.location.origin,
              "X-DHC-Hub": "digitalhustlerscaribbean.com",
            },
          }
        );

        login(response.data, response.data.token);
        toast.success(`Welcome, ${response.data.name || "User"}!`);

        let storedNext = null;
        try {
          storedNext = sessionStorage.getItem(AUTH_NEXT_STORAGE_KEY);
          sessionStorage.removeItem(AUTH_NEXT_STORAGE_KEY);
          sessionStorage.removeItem(OAUTH_EXCHANGE_LOCK_KEY);
        } catch {
          /* ignore */
        }

        const redirectPath = await resolvePostAuthRedirectPath({
          sessionUser,
          apiRole: response.data.role,
          storedNext,
          searchNext: searchParams.get("next"),
        });
        navigate(redirectPath, { replace: true });
      } catch (error) {
        console.error("Supabase auth callback error:", error);
        try {
          sessionStorage.removeItem(OAUTH_EXCHANGE_LOCK_KEY);
        } catch {
          /* ignore */
        }
        const message =
          error.response?.data?.detail ||
          error.message ||
          "Google sign-in failed. Try email and password.";
        toast.error(message);
        navigate("/login", { replace: true });
      }
    };

    finish();
  }, [login, navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-orange-600 mx-auto mb-4" />
        <p className="text-slate-600">Completing Google sign-in...</p>
      </div>
    </div>
  );
}
